import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useActivity } from "@/context/ActivityContext";
import { Flag } from "lucide-react";

export default function StartingGrid({ id }) {
  const { tick } = useActivity();
  const [list, setList] = useState([]);

  useEffect(() => {
    api.get(`/events/${id}/enrollments`).then((r) => setList(r.data)).catch(() => {});
  }, [id, tick]);

  if (!list.length) return null;

  return (
    <div className="mt-10" data-testid="starting-grid">
      <div className="flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-zinc-400">
        <Flag className="h-4 w-4 text-yellow-400" /> Griglia di partenza
        <span className="text-zinc-600">· {list.length} piloti</span>
      </div>

      <div className="mt-5 relative rounded-lg border border-zinc-800 bg-gradient-to-b from-zinc-900/60 to-zinc-950 p-6 overflow-hidden">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-3"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg,#fafafa 0 14px,#18181b 14px 28px)",
          }}
        />
        <div className="mt-4 space-y-3">
          {list.map((e, i) => {
            const pos = i + 1;
            const alignRight = i % 2 === 1;
            return (
              <motion.div
                key={e.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                data-testid={`grid-slot-${pos}`}
                className={`flex ${alignRight ? "justify-end" : "justify-start"}`}
              >
                <div className="flex items-center gap-3 w-full max-w-md">
                  {!alignRight && <GridNumber pos={pos} />}
                  <div
                    className={`flex-1 flex items-center gap-3 border px-4 py-3 rounded-md ${
                      pos === 1
                        ? "border-yellow-400/50 bg-yellow-400/10"
                        : "border-zinc-700 bg-zinc-900/70"
                    } ${alignRight ? "flex-row-reverse text-right" : ""}`}
                  >
                    <span
                      className={`font-heading font-black truncate ${
                        pos === 1 ? "text-yellow-300 text-lg" : "text-zinc-50"
                      }`}
                    >
                      {e.name}
                    </span>
                    {pos === 1 && (
                      <span className="text-[9px] font-bold tracking-[0.2em] uppercase text-yellow-400/80">
                        Pole
                      </span>
                    )}
                    {e.admin_added && (
                      <span className="text-[10px] text-cyan-400">(admin)</span>
                    )}
                  </div>
                  {alignRight && <GridNumber pos={pos} />}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function GridNumber({ pos }) {
  return (
    <span
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md font-display font-black text-lg tabular-nums ${
        pos === 1
          ? "bg-yellow-400 text-zinc-950"
          : "bg-zinc-800 text-zinc-300 border border-zinc-700"
      }`}
    >
      {pos}
    </span>
  );
}
